import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const serviceLinks = [
  { name: 'Pool Leak Detection', slug: 'pool-leak-detection-las-vegas' },
  { name: 'Water Leak Detection', slug: 'water-leak-detection-las-vegas' },
  { name: 'Home Repiping', slug: 'home-repiping-las-vegas' },
  { name: 'Mainline Replacement', slug: 'mainline-replacement-las-vegas' },
  { name: 'Slab Leak Repair', slug: 'slab-leak-repair-las-vegas' },
  { name: 'Emergency Plumbing', slug: 'emergency-plumbing-las-vegas' },
  { name: 'Water Heater Installation', slug: 'water-heater-installation-las-vegas' },
  { name: 'Water Filtration / Softener', slug: 'water-filtration-softener-las-vegas' },
  { name: 'Garbage Disposal Repair', slug: 'garbage-disposal-repair-las-vegas' },
  { name: 'Water Damage Restoration', slug: 'water-damage-restoration-las-vegas' },
  { name: 'Sewer Line Repair', slug: 'sewer-line-repair-las-vegas' },
  { name: 'Drain Cleaning', slug: 'drain-cleaning-las-vegas' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const textClass = isScrolled ? 'text-gray-700' : 'text-white';

  return (
    <>
      {/* Top Announcement Bar - Mobile Only */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-cyan-600 to-cyan-500 text-white py-2 px-4 text-center">
        <a href="tel:7252675482" className="flex items-center justify-center gap-2 text-sm font-bold whitespace-nowrap">
          <i className="ri-flashlight-fill text-base"></i>
          <span>24/7 Emergency Service — (725) 267-5482</span>
        </a>
      </div>

      <nav className={`fixed lg:top-0 top-10 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'lg:bg-transparent bg-[#0a1628]'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Mobile: Center Logo */}
            <div className="lg:hidden flex-1"></div>

            <Link to="/" className="flex items-center lg:flex-initial">
              <img
                src="https://static.readdy.ai/image/2f42848ae1d54308b2a19bc5b9c28bfc/52b6caa0ebdf1095639a765358c6ba7e.png"
                alt="Leak Detectives Logo"
                className="h-20 lg:h-32 w-auto object-contain"
              />
            </Link>

            {/* Mobile: Hamburger */}
            <div className="lg:hidden flex-1 flex justify-end">
              <button
                className="p-2"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-3xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
              </button>
            </div>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-6">
              {/* Services Dropdown */}
              <div ref={servicesRef} className="relative">
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className={`flex items-center gap-1 text-sm font-semibold hover:text-cyan-500 transition-colors whitespace-nowrap cursor-pointer ${textClass}`}
                >
                  Services
                  <i className={`ri-arrow-down-s-line text-base transition-transform ${isServicesOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isServicesOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[600px] bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 z-50">
                    <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3 px-1">All Services in Las Vegas</p>
                    <div className="grid grid-cols-2 gap-1">
                      {serviceLinks.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/${s.slug}`}
                          onClick={() => setIsServicesOpen(false)}
                          className="flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-cyan-50 hover:text-cyan-700 text-gray-700 transition-colors cursor-pointer group"
                        >
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className="ri-arrow-right-s-line text-cyan-500 group-hover:translate-x-0.5 transition-transform"></i>
                          </div>
                          <span className="text-sm font-medium">{s.name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <a href="/#why-choose-us" className={`text-sm font-semibold hover:text-cyan-500 transition-colors whitespace-nowrap ${textClass}`}>Why Us</a>
              <a href="/#service-areas" className={`text-sm font-semibold hover:text-cyan-500 transition-colors whitespace-nowrap ${textClass}`}>Areas</a>
              <a href="/#google-reviews" className={`text-sm font-semibold hover:text-cyan-500 transition-colors whitespace-nowrap ${textClass}`}>Reviews</a>
              <a href="tel:7252675482" className={`text-sm font-semibold hover:text-cyan-500 transition-colors whitespace-nowrap flex items-center gap-1 ${textClass}`}>
                <i className="ri-phone-fill text-cyan-500"></i> (725) 267-5482
              </a>
              <a href="tel:7252675482" className="bg-red-600 text-white px-5 py-3 rounded-lg font-bold text-sm hover:bg-red-500 transition-colors whitespace-nowrap flex items-center gap-2">
                <i className="ri-phone-fill"></i> Call Now
              </a>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[104px] bg-white z-40 overflow-y-auto">
            <div className="px-6 py-6 space-y-1">
              {/* Services Accordion */}
              <div>
                <button
                  className="w-full flex items-center justify-between text-lg font-bold text-gray-800 hover:text-cyan-500 py-4 border-b border-gray-200 cursor-pointer"
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                >
                  <span>Services</span>
                  <i className={`ri-arrow-down-s-line text-xl transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {isMobileServicesOpen && (
                  <div className="py-2 pl-4 space-y-1">
                    {serviceLinks.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/${s.slug}`}
                        className="flex items-center gap-2 text-base text-gray-700 hover:text-cyan-600 py-2"
                        onClick={() => { setIsMobileMenuOpen(false); setIsMobileServicesOpen(false); }}
                      >
                        <i className="ri-arrow-right-s-line text-cyan-500"></i>
                        {s.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <a href="/#why-choose-us" className="block text-lg font-bold text-gray-800 hover:text-cyan-500 py-4 border-b border-gray-200" onClick={() => setIsMobileMenuOpen(false)}>
                Why Choose Us
              </a>
              <a href="/#service-areas" className="block text-lg font-bold text-gray-800 hover:text-cyan-500 py-4 border-b border-gray-200" onClick={() => setIsMobileMenuOpen(false)}>
                Service Areas
              </a>
              <a href="/#google-reviews" className="block text-lg font-bold text-gray-800 hover:text-cyan-500 py-4 border-b border-gray-200" onClick={() => setIsMobileMenuOpen(false)}>
                Reviews
              </a>
              <a href="/#contact" className="block text-lg font-bold text-gray-800 hover:text-cyan-500 py-4 border-b border-gray-200" onClick={() => setIsMobileMenuOpen(false)}>
                Contact
              </a>

              <div className="pt-6 pb-4">
                <a
                  href="tel:7252675482"
                  className="block bg-red-600 text-white px-6 py-5 rounded-lg font-black text-xl text-center hover:bg-red-500 transition-colors flex items-center justify-center gap-3"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="ri-phone-fill text-2xl"></i>
                  <span className="whitespace-nowrap">(725) 267-5482</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
